import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { 
  Home, Package, Calendar, Truck, Shield, Users, 
  AlertTriangle, TrendingUp, Bell, Settings, User,
  Wifi, WifiOff, RefreshCw, CheckCircle, Clock
} from 'lucide-react';

const MobileDashboard = () => {
  const { user, role, isOnline, canAccessFeature, getMobileNavigation } = useMobileAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [role]);

  const fetchDashboardData = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/mobile/dashboard', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const getRoleBasedWidgets = () => {
    const widgets = [];

    // Common widgets for all roles
    widgets.push(
      <div key="status" className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">Connection Status</h3>
          {isOnline ? <Wifi className="w-5 h-5 text-green-500" /> : <WifiOff className="w-5 h-5 text-red-500" />}
        </div>
        <p className="text-sm text-gray-600">
          {isOnline ? 'Connected and synced' : 'Offline - changes will sync when online'}
        </p>
      </div>
    );

    // Role-specific widgets
    if (canAccessFeature('inventory')) {
      widgets.push(
        <div key="inventory" className="bg-blue-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-blue-900">Inventory</h3>
            <Package className="w-5 h-5 text-blue-600" />
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-blue-900">{dashboardData.lowStockItems || 0}</p>
            <p className="text-sm text-blue-700">Low stock items</p>
            {dashboardData.expiringItems > 0 && (
              <div className="flex items-center mt-2">
                <AlertTriangle className="w-4 h-4 text-orange-500 mr-1" />
                <span className="text-sm text-orange-700">{dashboardData.expiringItems} expiring soon</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (canAccessFeature('bookings')) {
      widgets.push(
        <div key="bookings" className="bg-green-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-green-900">Bookings</h3>
            <Calendar className="w-5 h-5 text-green-600" />
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-green-900">{dashboardData.todayBookings || 0}</p>
            <p className="text-sm text-green-700">Today's bookings</p>
            <p className="text-sm text-green-600">{dashboardData.availableRooms || 0} rooms available</p>
          </div>
        </div>
      );
    }

    if (canAccessFeature('delivery')) {
      widgets.push(
        <div key="delivery" className="bg-orange-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-orange-900">Delivery</h3>
            <Truck className="w-5 h-5 text-orange-600" />
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-orange-900">{dashboardData.activeDeliveries || 0}</p>
            <p className="text-sm text-orange-700">Active deliveries</p>
            <p className="text-sm text-orange-600">{dashboardData.availableDrivers || 0} drivers available</p>
          </div>
        </div>
      );
    }

    if (canAccessFeature('safety')) {
      widgets.push(
        <div key="safety" className="bg-red-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-red-900">Safety</h3>
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-red-900">
              {dashboardData.complianceStatus === 'NOT_ASSESSED' ? 'Not assessed' : `${dashboardData.complianceRate}%`}
            </p>
            <p className="text-sm text-red-700">Compliance status</p>
            {dashboardData.pendingTasks > 0 && (
              <div className="flex items-center mt-2">
                <Clock className="w-4 h-4 text-orange-500 mr-1" />
                <span className="text-sm text-orange-700">{dashboardData.pendingTasks} tasks pending</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (canAccessFeature('events')) {
      widgets.push(
        <div key="events" className="bg-purple-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-purple-900">Events</h3>
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-purple-900">{dashboardData.upcomingEvents || 0}</p>
            <p className="text-sm text-purple-700">Upcoming events</p>
            <p className="text-sm text-purple-600">Proposal count not connected</p>
          </div>
        </div>
      );
    }

    if (canAccessFeature('analytics')) {
      widgets.push(
        <div key="analytics" className="bg-indigo-50 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-indigo-900">Analytics</h3>
            <TrendingUp className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-indigo-900">Revenue not connected</p>
            <p className="text-sm text-indigo-700">Today's revenue</p>
            <p className="text-sm text-indigo-600">Occupancy not connected</p>
          </div>
        </div>
      );
    }

    return widgets;
  };

  const getQuickActions = () => {
    const actions = [];

    if (canAccessFeature('inventory')) {
      actions.push({
        name: 'Scan Item',
        icon: Package,
        color: 'blue',
        action: () => navigate('/mobile/inventory', { state: { action: 'scan' } })
      });
    }

    if (canAccessFeature('bookings')) {
      actions.push({
        name: 'New Booking',
        icon: Calendar,
        color: 'green',
        action: () => navigate('/mobile/bookings', { state: { action: 'new' } })
      });
    }

    if (canAccessFeature('delivery')) {
      actions.push({
        name: 'Start Delivery',
        icon: Truck,
        color: 'orange',
        action: () => navigate('/mobile/delivery', { state: { action: 'start' } })
      });
    }

    if (canAccessFeature('safety')) {
      actions.push({
        name: 'Log Temperature',
        icon: Shield,
        color: 'red',
        action: () => navigate('/mobile/safety', { state: { tab: 'temperature' } })
      });
    }

    return actions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500">Welcome back, {user?.displayName}</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              className={`p-2 rounded-full ${refreshing ? 'animate-spin' : ''}`}
              disabled={refreshing}
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
            <button className="p-2 rounded-full relative">
              <Bell className="w-5 h-5 text-gray-600" />
              {dashboardData.unreadNotifications > 0 && (
                <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>
            <button className="p-2 rounded-full">
              <User className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Quick Actions */}
        {getQuickActions().length > 0 && (
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-3">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              {getQuickActions().map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className={`flex flex-col items-center justify-center p-4 rounded-lg bg-${action.color}-50 hover:bg-${action.color}-100 transition-colors`}
                  >
                    <Icon className={`w-6 h-6 text-${action.color}-600 mb-2`} />
                    <span className={`text-sm font-medium text-${action.color}-900`}>{action.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Role-Based Widgets */}
        <div className="grid grid-cols-1 gap-4">
          {getRoleBasedWidgets()}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-3">Recent Activity</h2>
          <div className="space-y-3">
            {dashboardData.recentActivity?.length ? dashboardData.recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'success' ? 'bg-green-500' :
                  activity.type === 'warning' ? 'bg-yellow-500' :
                  activity.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.message}</p>
                  <p className="text-xs text-gray-500">{activity.timestamp}</p>
                </div>
              </div>
            )) : (
              <p className="text-sm text-gray-500">
                {dashboardData.recentActivityStatus === 'NOT_CONFIGURED' ? 'Activity feed not connected' : 'No recent activity'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileDashboard;
